import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import C from './JamDetailsModal.css';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, CircularProgress, useTheme } from '@mui/material';
import Button from '@mui/material/Button';

import FlexCol from 'components/flex/FlexCol';
import FlexRow from 'components/flex/FlexRow';
import CloseableDialog from 'components/modal-dialog/CloseableDialog';
import VideoCard from 'components/video-card/VideoCard';

import IconEye from '!!react-svg-loader!../space-details-modal/icons/icon--show.svg';

import { useCommunityDetails } from 'atoms/community-details';
import { Jam, getJamsLocal } from 'atoms/jams';
import CoCreatorCountTag from '../jam-card/CoCreatorCountTag';
import CountryOrCocoTag from '../jam-card/CountryOrCocoTag';
import { Trans, msg, t } from '@lingui/macro';
import { useSpaceSummary, useSpacesQuery } from 'atoms/spaces';
import _ from 'lodash';
import { reportServerError } from 'utils/report-error';
import { useUserProfile } from 'atoms/user-profile';
import { httpClient } from '@cocoplatform/coco-rtc-client';
import RemixIcon from '!!react-svg-loader!../world-corridor-panel/icons/icon--space-remix.svg';
import { ja } from 'date-fns/locale';
import { EVENTS, INTERACTIONS, useTrack } from 'utils/mixpanel';
import { getThemeLocalizedNames } from 'atoms/space-themes';
import { useAuthState } from 'utils/auth-state';
import { useModal } from 'mui-modal-provider';
import PlayIcon from '!!react-svg-loader!../home-page/play-2.svg';
import { useFlags } from 'utils/feature-flags';

export const JAM_TRANSLATION_MAP = () => ({
  'Introducing CoCo Spaces': {
    title: t`Introducing CoCo Spaces`,
    description: t`Learn more about the collaborative creative coding possibilities you can explore in a CoCo Space.`,
    caption: 'getting-started-en.vtt',
  },
  'Two-Player Basketball': {
    title: t`Two-Player Basketball`,
    description: t`Code a two player basketball game! One creator passes the ball, the other has to prevent the goal. Explore how to keep a shared score.`,
  },
  'Collaborative Catch Game': {
    title: t`Collaborative Catch Game`,
    description: t`Build a collaborative catch game. You can use Makey Makey to explore physical computing OR press arrow keys to make objects fall in a friend's project over live video!`,
  },
  'Interactive Pen Art': {
    title: t`Interactive Pen Art`,
    description: t`Use the wave block to make a collaborative interactive art with four creators!`,
  },
  'Pass a Birthday Hat': {
    title: t`Pass a Birthday Hat`,
    description: t`Use video sensing and pass a birthday hat to a friend in real-time!`,
  },
  'Make Music Together': {
    title: t`Make Music Together`,
    description: t`Code a music jam together and play instruments on live video with friends in real-time!`,
  },
  'Collaborative Story': {
    title: t`Collaborative Story`,
    description: t`Create a collaborative story with peers! Make characters fly through projects, animate objects, and add dialogues!`,
  },
  'Multiplayer Pac-Man Game': {
    title: t`Multiplayer Pac-Man Game`,
    description: t`In this 4 player game, each creator programs their part of the board. The character passes from one player's project to another while avoiding the ghosts.`,
  },
});

export function JamDetailsCard(p: {
  isEditing?: boolean;
  onNext?: (jamId: string) => void;
  onPrev?: (jamId: string) => void;
}) {
  let { jamId, id, communityId, handle } = useParams<{
    jamId: string | undefined;
    communityId: string;
    id: string | undefined;
    handle: string | undefined;
  }>();
  const history = useHistory();

  const { isSignedIn } = useAuthState();
  const { showModal } = useModal();
  const { play_with_friends } = useFlags(['play_with_friends']);

  if (!id && !jamId) {
    return <></>;
  }
  if (!jamId) {
    jamId = id;
  }

  const {
    fetchInitialSpaces,
    data: { spaces: jams },
    syncState,
  } = useSpacesQuery({
    sectionId: 'coco-jams',
    sortCriteria: 'expectedToStartAt',
    filter: ['coco-jams', 'all'],
    sortDir: 'desc',
  });

  useEffect(() => {
    if (syncState === 'pending') {
      fetchInitialSpaces();
    }
  }, []);

  const { userProfile } = useUserProfile();

  const jamIndex = jams?.findIndex((jam) => jam.id === jamId);
  const {
    space: jam,
    patchLocal: patchSpaceDetails,
    refetch,
  } = useSpaceSummary({
    spaceId: jamId || '',
  });
  const { palette } = useTheme();
  const { community: communityDetails, loadCommunityDetails } =
    useCommunityDetails({
      communityId: jam?.communityId || '',
      autoLoad: true,
    });

  useEffect(() => {
    if (handle) {
      refetch();
    }
  }, []);

  const track = useTrack();

  if (!jam)
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <CircularProgress />
      </Box>
    );

  async function exploreSpace(spaceId: string, userName: string) {
    try {
      const response = await httpClient.post(`/spaces/${spaceId}/participant`, {
        userName:
          (userName || 'Guest') + Math.random().toString(36).substring(7),
      });

      if (response?.data?.token) {
        sessionStorage.setItem('participantToken', response.data.token);
      }
    } catch (error) {
      reportServerError({
        title: 'Failed to add participant',
        error,
      });
      return;
    }
    patchSpaceDetails(spaceId, (s) => {
      return { ...s, userName };
    });
    // This currently needs to be a full page refresh because
    // coco-gui doesn't cleanup the redux state
    location.href = `/app/spaces/${spaceId}/editor?explore=true`;
  }

  const translatedJam = JAM_TRANSLATION_MAP()[jam?.title || ''] || jam;

  return (
    <>
      {jam ? (
        <>
          <div
            className={C.cardContainer}
            data-space-id={jamId}
            style={{
              border: 'none',
            }}
          >
            {jam.jamVideoUrl ? (
              <VideoCard
                logo={'/' + jam.thumbnail}
                videoSrc={'/' + jam.jamVideoUrl}
                controls
                autoPlay
                style={{
                  objectFit: 'contain',
                }}
                caption={jam?.caption}
              />
            ) : (
              <Box
                sx={{
                  height: 0,
                  paddingTop: '56.25%',
                  width: '100%',
                  backgroundColor: 'black',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  position: 'relative',
                }}
              >
                <img
                  src={jam.thumbnail ? '/' + jam.thumbnail : jam?.bannerUrl}
                  alt={jam.title}
                  style={{
                    objectFit: 'contain',
                    height: '100%',
                    position: 'absolute',
                    top: '0',
                  }}
                />
              </Box>
            )}

            <div className={C.cardDetails}>
              <FlexCol alignItems={'left'} className={C.detailsContainer}>
                <FlexRow style={{ gap: 8 }}>
                  <CoCreatorCountTag jam={jam} />
                  <div className={C.spaceTitle}>{translatedJam?.title}</div>
                  {jam?.spaceId && (
                    <>
                      {play_with_friends?.enabled && (
                        <Button
                          variant='contained'
                          onClick={async () => {
                            track(EVENTS.EXPLORE_JAM, INTERACTIONS.CLICK, {
                              jamId: jam.id,
                              jamTitle: jam.title,
                              action: 'play_with_friends',
                            });
                            try {
                              const response = await httpClient.post(
                                `/spaces/${jam.spaceId}/participant`,
                                {
                                  userName:
                                    (userProfile?.username ||
                                      userProfile?.firstName ||
                                      'Guest') +
                                    Math.random().toString(36).substring(7),
                                },
                              );

                              if (response?.data?.token) {
                                sessionStorage.setItem(
                                  'participantToken',
                                  response.data.token,
                                );
                              }
                            } catch (error) {
                              reportServerError({
                                title: 'Failed to add participant',
                                error,
                              });
                              return;
                            }
                            patchSpaceDetails(jam.spaceId, (s) => {
                              return {
                                ...s,
                                userName:
                                  userProfile?.username ||
                                  userProfile?.firstName ||
                                  'Guest',
                              };
                            });
                            // Navigate to editor with playWithFriends parameter
                            location.href = `/app/spaces/${jam.spaceId}/editor?explore=true&playWithFriends=true`;
                          }}
                          sx={{
                            height: '35px',
                            backgroundColor: '#4CAF50', // Using success green similar to menu-bar
                            color: '#ffffff',
                            '&:hover': {
                              backgroundColor: '#45a049',
                            },
                          }}
                          startIcon={<PlayIcon className={C.playIcon} />}
                          id='play-with-friends-btn'
                        >
                          <span>
                            <Trans>Play with Friends</Trans>
                          </span>
                        </Button>
                      )}
                      <Button
                        variant='contained'
                        onClick={async () => {
                          // Note: We will instead support exploring a space
                          // anonymously
                          // if (!isSignedIn) {
                          //   const modal = showModal(SignupCoCoModal, {
                          //     onClose: () => {
                          //       modal.destroy();
                          //     },
                          //   });
                          //   return;
                          // }
                          track(EVENTS.EXPLORE_JAM, INTERACTIONS.CLICK, {
                            jamId: jam.id,
                            jamTitle: jam.title,
                          });
                          exploreSpace(
                            jam.spaceId,
                            userProfile?.username ||
                            userProfile?.firstName ||
                            'Guest',
                          );
                        }}
                        sx={{
                          height: '35px',
                          backgroundColor: palette.primary.main,
                          '&:hover': {
                            backgroundColor: palette.primary.main,
                          },
                        }}
                        className={C.viewSpaceContainer}
                        startIcon={
                          <RemixIcon
                            color={'#fff'}
                            className={C.viewSpaceIcon}
                          />
                        }
                        id='view-space-btn'
                      >
                        <span>
                          <Trans>Explore Space</Trans>
                        </span>
                      </Button>
                    </>
                  )}
                </FlexRow>
                <FlexRow>
                  <div className={C.text}>{translatedJam?.description}</div>
                </FlexRow>
                <div className={C.bottomRow}>
                  {jam.themes && (
                    <ul className={C.themeTags}>
                      {jam.themes.map((tag) => {
                        return (
                          <li key={tag}>
                            <div
                              style={{
                                cursor: 'pointer',
                              }}
                              className={C.themeTag}
                            >
                              {getThemeLocalizedNames()[tag?.toLowerCase()]}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {!handle && (
                    <>
                      <div className={C.communityText}>
                        <CountryOrCocoTag
                          jam={jam}
                          tooltipProps={{ placement: 'top' }}
                        />
                        <span style={{ marginLeft: 8 }}>
                          {' '}
                          <Trans>by</Trans>{' '}
                          {`${jam?.createdByName || 'CoCo Team'}`}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </FlexCol>
              {!!jam.spaceId && <div className={C.enterContainer}></div>}
            </div>
          </div>
          <div>
            {jamIndex != null && jamIndex > 0 && !handle && (
              <a
                className={C.snapshotLeftNav}
                onClick={() => {
                  if (jamIndex == 0) return;
                  if (p.onPrev) {
                    p.onPrev(jams[jamIndex - 1].id);
                    return;
                  }
                  history.push(
                    `/app/dashboard/${communityId}/jams/${jams[jamIndex - 1].id}`,
                  );
                }}
              >
                <ChevronLeftIcon />
              </a>
            )}
            {jams &&
              jams.length > 0 &&
              !handle &&
              (jamIndex == null || jamIndex < jams.length - 1) && (
                <a
                  className={C.snapshotRightNav}
                  onClick={() => {
                    if (jamIndex == null || jamIndex >= jams.length - 1) return;
                    if (p.onNext) {
                      p.onNext(jams[jamIndex + 1].id);
                      return;
                    }
                    history.push(
                      `/app/dashboard/${communityId}/jams/${jams[jamIndex + 1].id}`,
                    );
                  }}
                >
                  <ChevronRightIcon />
                </a>
              )}
          </div>
        </>
      ) : (
        <></>
      )}
    </>
  );
}

export default function JamDetailsModal(p: {
  isEditing?: boolean;
  onClose?: () => void;
  onNext?: (jamId: string) => void;
  onPrev?: (jamId: string) => void;
}) {
  const history = useHistory();

  const { communityId } = useParams<{ communityId: string }>();

  function handleExit() {
    history.push(`/app/dashboard/${communityId}/spaces`);
  }

  return (
    <CloseableDialog
      onClose={p.onClose ?? handleExit}
      className={C.dialog}
      fullWidth
      maxWidth='md'
    >
      <JamDetailsCard
        isEditing={p.isEditing}
        onNext={p.onNext}
        onPrev={p.onPrev}
      />
    </CloseableDialog>
  );
}
